import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { motion } from "framer-motion";
import { Lock, ArrowLeft } from "lucide-react";
import CategorizedGallery from "../components/CategorizedGallery";
import OfferForm from "../components/landing/OfferForm";
import SpecialProjectConfigurator from "../components/SpecialProjectConfigurator";
import InterviewEmbed from "../components/InterviewEmbed";
import { useLocale } from "../i18n/LocaleContext";
import { useAuth } from "../contexts/AuthContext";
import { useTranslated } from "../lib/useTranslated";

export default function DropDetail() {
  const { slug } = useParams();
  const [drop, setDrop] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLocale();
  const { isMember } = useAuth();
  const trTitle = useTranslated(drop?.title || "");
  const trSubtitle = useTranslated(drop?.subtitle || "");
  const trIntro = useTranslated(drop?.intro || "");
  const trStory = useTranslated(drop?.story || "");

  useEffect(() => {
    api
      .get(`/drops/${slug}`)
      .then((r) => setDrop(r.data))
      .catch(() => setDrop(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="overline">{t("drops.loading")}</span>
      </div>
    );

  if (!drop)
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <span className="overline mb-4 block">404</span>
          <h1 className="text-3xl font-light mb-6">{t("drop.notFound")}</h1>
          <Link to="/drops" className="gd-button">
            {t("drop.backToVault")}
          </Link>
        </div>
      </div>
    );

  const locked = drop.locked;
  // A drop is treated as "for sale" when either:
  //   • it is the live featured drop (cinematic vetrina), OR
  //   • the admin set its sale_status to "for_sale" (sells at a price)
  //   • the admin set its sale_status to "accepting_offers" (members can
  //     submit an offer even without a fixed price)
  // Anything tagged "coming_soon" or "not_for_sale" stays in display-only.
  const saleStatus = drop?.sale_status || "for_sale";
  const showsPrice = drop?.is_featured || saleStatus === "for_sale";
  const isForSale =
    Boolean(drop?.is_featured) ||
    saleStatus === "for_sale" ||
    saleStatus === "accepting_offers";

  // The "hero image" field accepts both images and videos. Detect video by
  // file extension so we render a <video> tag instead of <img> (which would
  // just paint a black rectangle for any non-image source).
  const heroIsVideo = /\.(mp4|webm|mov|m4v|ogg|ogv)(\?|#|$)/i.test(
    drop.hero_image || "",
  );

  return (
    <div className="pb-24" data-testid="drop-detail-page" onContextMenu={(e) => { if (e.target.tagName === "IMG" || e.target.tagName === "VIDEO") e.preventDefault(); }}>
      <section className="relative h-[80vh] sm:h-screen overflow-hidden gd-noselect">
        {heroIsVideo ? (
          <video
            src={drop.hero_image}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: locked ? "blur(28px) brightness(0.35)" : "brightness(0.6)" }}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onLoadedMetadata={(e) => {
              try {
                if (e.currentTarget.currentTime < 0.05) {
                  e.currentTarget.currentTime = 0.1;
                }
              } catch {
                /* best-effort poster frame */
              }
            }}
          />
        ) : (
          <img
            src={drop.hero_image}
            alt={drop.title}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: locked ? "blur(28px) brightness(0.35)" : "brightness(0.6)" }}
            draggable={false}
          />
        )}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,5,0.4) 0%, rgba(5,5,5,0.6) 60%, rgba(5,5,5,1) 100%)",
          }}
        />

        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 sm:px-12 flex flex-col justify-end pb-20" style={{ color: "#fff" }}>
          <Link
            to="/drops"
            className="overline text-[10px] mb-6 inline-flex items-center gap-2"
            style={{ color: "rgba(255,255,255,0.7)" }}
            data-testid="drop-back-link"
          >
            <ArrowLeft size={12} strokeWidth={1.5} />
            {t("drop.backLink")}
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <span className="overline" style={{ color: "rgba(255,255,255,0.8)" }}>Drop {drop.number}</span>
            {!locked && !drop.teaser && (
              isForSale ? (
                <span className="flex items-center gap-2">
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: "#34C759", boxShadow: "0 0 0 4px rgba(52,199,89,0.22)" }}
                  />
                  <span className="overline text-[10px]" style={{ color: "#34C759" }}>
                    {t("drops.live")}
                  </span>
                </span>
              ) : (
                <span className="flex items-center gap-2" data-testid="drop-in-catalog-badge">
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: "rgba(255,255,255,0.55)" }}
                  />
                  <span className="overline text-[10px]" style={{ color: "rgba(255,255,255,0.7)" }}>
                    {t("drop.inCatalogOverline")}
                  </span>
                </span>
              )
            )}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="text-4xl sm:text-7xl text-balance mb-6 max-w-4xl"
            style={{ letterSpacing: "-0.022em", lineHeight: 1.05, color: "#fff", fontWeight: 600 }}
            data-testid="drop-title"
          >
            {trTitle || drop.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-base sm:text-xl max-w-2xl"
            style={{ color: "rgba(255,255,255,0.85)", fontWeight: 400 }}
          >
            {drop.subtitle}
          </motion.p>
        </div>
      </section>

      {locked ? (
        <section className="py-32 px-6 sm:px-12 text-center max-w-2xl mx-auto">
          <Lock size={32} strokeWidth={1} className="mx-auto mb-8" style={{ color: "var(--text-secondary)" }} />
          <span className="overline mb-4 block">{t("drop.lockedOverline")}</span>
          <h2 className="text-3xl sm:text-4xl font-light mb-6 text-balance">
            {t("drop.lockedHeadline")}
          </h2>
          <p className="text-sm mb-10" style={{ color: "var(--text-secondary)" }}>
            {t("drop.lockedBody")}
          </p>
          <Link to="/" className="gd-button" data-testid="locked-cta-home">
            {t("drop.backHome")}
          </Link>
        </section>
      ) : drop.teaser ? (
        /* Upcoming drop: only a single clue, no gallery / price / offer form.
           The hero image at the top is the "indizio" — everything else stays
           hidden until the drop goes live. */
        <section
          className="py-32 px-6 sm:px-12 text-center max-w-2xl mx-auto"
          data-testid="drop-teaser-section"
        >
          <span
            className="overline mb-6 block"
            style={{ color: "var(--accent-red)" }}
          >
            ● {t("drop.teaserOverline")}
          </span>
          <h2 className="text-3xl sm:text-4xl font-light mb-6 text-balance">
            {t("drop.teaserHeadline")}
          </h2>
          <p className="text-sm mb-10" style={{ color: "var(--text-secondary)" }}>
            {t("drop.teaserBody")}
          </p>
          <Link to="/" className="gd-button" data-testid="teaser-cta-home">
            {t("drop.backHome")}
          </Link>
        </section>
      ) : (
        <>
          <CategorizedGallery drop={drop} />

          {drop.intro && (
            <section className="py-32 sm:py-40 px-6 sm:px-12">
              <div className="max-w-3xl mx-auto">
                <span
                  className="text-[10px] uppercase block mb-8"
                  style={{ color: "var(--text-muted)", letterSpacing: "0.4em", fontWeight: 300 }}
                >
                  {t("drop.object")}
                </span>
                <p
                  className="text-2xl sm:text-4xl font-extralight leading-snug text-balance"
                  style={{ color: "var(--text-primary)", letterSpacing: "-0.01em" }}
                >
                  {trIntro || drop.intro}
                </p>
              </div>
            </section>
          )}

          {drop.specs && Object.keys(drop.specs).length > 0 && (
            <section className="py-24 sm:py-32 px-6 sm:px-12">
              <div className="max-w-5xl mx-auto">
                <span
                  className="text-[10px] uppercase block mb-14"
                  style={{ color: "var(--text-muted)", letterSpacing: "0.4em", fontWeight: 300 }}
                >
                  {t("drop.specs")}
                </span>
                <div className="grid sm:grid-cols-2 gap-x-20 gap-y-10">
                  {Object.entries(drop.specs).map(([key, value]) => (
                    <div
                      key={key}
                      className="pb-5 border-b"
                      style={{ borderColor: "rgba(255,255,255,0.06)" }}
                    >
                      <span
                        className="text-[10px] uppercase mb-2 block"
                        style={{ color: "var(--text-muted)", letterSpacing: "0.25em" }}
                      >
                        {key.replace(/_/g, " ")}
                      </span>
                      <span
                        className="text-lg sm:text-xl font-extralight"
                        style={{ color: "var(--text-primary)" }}
                      >
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {drop.interview_youtube_id && (
            <InterviewEmbed
              youtubeId={drop.interview_youtube_id}
              title={drop.title}
            />
          )}

          {drop.story && (
            <section className="py-32 sm:py-40 px-6 sm:px-12">
              <div className="max-w-2xl mx-auto">
                <span
                  className="text-[10px] uppercase block mb-10"
                  style={{ color: "var(--text-muted)", letterSpacing: "0.4em", fontWeight: 300 }}
                >
                  {t("drop.story")}
                </span>
                <p
                  className="text-base sm:text-lg leading-relaxed text-balance"
                  style={{ color: "var(--text-secondary)", lineHeight: 1.8 }}
                >
                  {drop.story}
                </p>
              </div>
            </section>
          )}

          {drop.restoration_sections && drop.restoration_sections.length > 0 && (
            <section
              className="py-32 sm:py-40 px-6 sm:px-12"
              data-testid="drop-restoration"
            >              <div className="max-w-4xl mx-auto">
                <span
                  className="text-[10px] uppercase block mb-10"
                  style={{ color: "var(--text-muted)", letterSpacing: "0.4em", fontWeight: 300 }}
                >
                  {t("drop.restoration")}
                </span>
                <h2
                  className="text-3xl sm:text-5xl font-extralight mb-16 text-balance"
                  style={{ letterSpacing: "-0.015em" }}
                >
                  {t("drop.restorationHeadline")}
                </h2>
                <div className="space-y-16">
                  {drop.restoration_sections.map((sec, idx) => (
                    <article
                      key={`${sec.title}-${idx}`}
                      className="grid sm:grid-cols-[180px_1fr] gap-6 sm:gap-12"
                      data-testid={`drop-restoration-section-${idx}`}
                    >
                      <div>
                        <span
                          className="text-[10px] font-mono tabular-nums block mb-3"
                          style={{ color: "var(--text-muted)" }}
                        >
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <h3
                          className="text-xl sm:text-2xl font-extralight"
                          style={{ color: "var(--text-primary)" }}
                        >
                          {sec.title}
                        </h3>
                      </div>
                      <p
                        className="text-base leading-relaxed whitespace-pre-wrap"
                        style={{ color: "var(--text-secondary)", lineHeight: 1.8 }}
                      >
                        {sec.body}
                      </p>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Special-project configurator — appears whenever the admin
              tagged this drop as a buildable project (e.g. Spiaggina). It
              replaces the standard price / offer block because the
              configurator already has its own enquiry form. */}
          {drop.is_special_project ? (
            <SpecialProjectConfigurator drop={drop} />
          ) : isForSale ? (
            <>
              {showsPrice && (
                <section
                  className="py-32 sm:py-40 px-6 sm:px-12 text-center"
                  data-testid="drop-price-section"
                >
                  <span
                    className="text-[10px] uppercase mb-6 block"
                    style={{ color: "var(--text-muted)", letterSpacing: "0.4em", fontWeight: 300 }}
                  >
                    {t("drop.price")}
                  </span>
                  {isMember && drop.member_price?.trim() && drop.price?.trim() ? (
                    <>
                      <p
                        className="text-2xl sm:text-3xl font-extralight"
                        data-testid="drop-price-public-strike"
                        style={{
                          letterSpacing: "-0.01em",
                          color: "#FF2800",
                          textDecoration: "line-through",
                          textDecorationColor: "#FF2800",
                          textDecorationThickness: "2px",
                          opacity: 0.85,
                          marginBottom: 10,
                        }}
                      >
                        {drop.price.trim()}
                      </p>
                      <p
                        className="text-4xl sm:text-6xl font-extralight"
                        style={{ letterSpacing: "-0.02em" }}
                        data-testid="drop-price"
                      >
                        {drop.member_price.trim()}
                      </p>
                      <span
                        className="text-[10px] uppercase mt-5 block"
                        style={{
                          color: "#FF2800",
                          letterSpacing: "0.35em",
                          fontWeight: 500,
                        }}
                        data-testid="drop-price-member-badge"
                      >
                        ● Prezzo riservato ai membri
                      </span>
                    </>
                  ) : (
                    <p
                      className="text-4xl sm:text-6xl font-extralight"
                      style={{ letterSpacing: "-0.02em" }}
                      data-testid="drop-price"
                    >
                      {drop.price?.trim() || t("drop.priceOnRequest")}
                    </p>
                  )}
                </section>
              )}

              {/* CTA WhatsApp + Newsletter — semplice, senza email/mail form
                  e senza "diventa membro". Vendita tra privati: si parla su WA. */}
              <DropContactCTA drop={drop} />
            </>
          ) : (
            <DropContactCTA drop={drop} />
          )}
        </>
      )}
    </div>
  );
}

function DropContactCTA({ drop }) {
  const WHATSAPP_HREF = "https://wa.me/393520463627";
  const msg = `Ciao, mi interessa ${drop?.title || "questo pezzo"}${
    drop?.year ? " (" + drop.year + ")" : ""
  }. Sarebbe possibile avere info?`;
  const waHref = `${WHATSAPP_HREF}?text=${encodeURIComponent(msg)}`;
  return (
    <section
      className="py-24 sm:py-32 px-6 sm:px-12 text-center max-w-3xl mx-auto"
      data-testid="drop-contact-cta"
    >
      <span
        className="text-[10px] uppercase mb-8 block"
        style={{ color: "var(--text-muted)", letterSpacing: "0.4em", fontWeight: 300 }}
      >
        Info · Trattativa · Visita
      </span>
      <h2
        className="text-3xl sm:text-5xl font-extralight mb-6 text-balance"
        style={{ letterSpacing: "-0.015em" }}
      >
        Si parla su WhatsApp.
      </h2>
      <p
        className="text-sm sm:text-base mb-10 max-w-xl mx-auto"
        style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}
      >
        Senza intermediari. Chatta direttamente con noi:
        rispondiamo subito con foto, video e disponibilità della visita a Brescia.
      </p>
      <a
        href={waHref}
        target="_blank"
        rel="noreferrer"
        className="inline-block px-8 py-4 text-[14px] tracking-wide"
        style={{ background: "#25D366", color: "#FFFFFF", fontWeight: 500 }}
        data-testid="drop-wa-cta"
      >
        Scrivi su WhatsApp
      </a>
      <p
        className="text-[13px] mt-8"
        style={{ color: "var(--text-secondary)" }}
      >
        Vuoi essere avvisato quando entra un nuovo pezzo?{" "}
        <a
          href="/#newsletter"
          className="underline"
          style={{ color: "#FF2800" }}
          data-testid="drop-newsletter-link"
        >
          Iscriviti alla newsletter
        </a>
        .
      </p>
    </section>
  );
}
